import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudQueue as CloudIcon,
  Storage as DatabaseIcon,
  List as ListIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import HubStatusBadge from './HubStatusBadge';
import InstanceExpandedTabs from './InstanceExpandedTabs';
import HubMicroservicePanel from './HubMicroservicePanel';
import {
  getTestInstances,
  createTestInstance,
  updateTestInstance,
  deleteTestInstance,
  fetchTestInstanceCBV,
  fetchTestInstanceMSV,
  fetchTestInstanceAzure,
  getEnabledTeams,
  getInstanceManagementNamespaces,
  getInstanceMonitorHubStatus,
} from '../../services/apiService';
import { isAdmin, isAuthenticated, canPerformQAActions } from '../../services/authService';

/** Extract Azure service name from base URL (e.g. host qafet1dealcloserintegration1zus1.zus1.highqsolutions.com → qafet1dealcloserintegration1zus1) */
const getAzureServiceNameFromBaseUrl = (baseURL) => {
  if (!baseURL || !String(baseURL).trim()) return '';
  try {
    const url = new URL(baseURL.startsWith('http') ? baseURL : `http://${baseURL}`);
    return url.hostname.split('.')[0] || '';
  } catch {
    return '';
  }
};

/** Build getCBV.action URL from base URL */
const getCBVActionUrl = (baseURL) => {
  if (!baseURL || !String(baseURL).trim()) return '';
  const base = baseURL.trim().replace(/\/$/, '');
  return `${base}/getCBV.action`;
};

/** Determine instance type: Legacy, Modern, or Unknown.
 *  Priority: persisted instanceType field > azureData flag > cbvData.instanceType */
const getInstanceType = (instance) => {
  // 1. Persisted field set by Azure fetch (most reliable)
  if (instance?.instanceType && instance.instanceType !== 'Unknown') {
    return instance.instanceType;
  }
  // 2. Live azureData in memory (set during the same session before save)
  if (instance?.azureData?.['Isheet modernisation (Backend)'] != null) {
    const isModern = String(instance.azureData['Isheet modernisation (Backend)']).trim().toUpperCase() === 'TRUE';
    return isModern ? 'Modern' : 'Legacy';
  }
  // 3. CBV instanceType as last resort
  const cbvType = instance?.cbvData?.instanceType;
  if (cbvType != null && String(cbvType).trim()) {
    const t = String(cbvType).trim();
    if (/modern/i.test(t)) return 'Modern';
    if (/legacy/i.test(t)) return 'Legacy';
  }
  return 'Unknown';
};

/**
 * TestInstancesView component for managing test instances
 * @returns {JSX.Element} TestInstancesView component
 */
const TestInstancesView = () => {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingInstance, setEditingInstance] = useState(null);
  const [fetchingIds, setFetchingIds] = useState({}); // Track which instances are fetching data
  const [msvDialogOpen, setMsvDialogOpen] = useState(false);
  const [selectedInstanceForMsv, setSelectedInstanceForMsv] = useState(null);
  const [cbvDialogOpen, setCbvDialogOpen] = useState(false);
  const [selectedInstanceForCbv, setSelectedInstanceForCbv] = useState(null);
  const [azureDialogOpen, setAzureDialogOpen] = useState(false);
  const [selectedInstanceForAzure, setSelectedInstanceForAzure] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [instanceTypeFilter, setInstanceTypeFilter] = useState('All');
  const [branchFilter, setBranchFilter] = useState('');
  const [usageFilter, setUsageFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [syncingAll, setSyncingAll] = useState(false);
  const [teams, setTeams] = useState([]);
  const [expandedCards, setExpandedCards] = useState({}); // Track which rows are expanded
  const [formData, setFormData] = useState({
    baseURL: '',
    contextName: '',
    usage: '',
    team: '',
    activity: ''
  });
  // Services tab (read-only Instance Management tree)
  const [servicesTab, setServicesTab] = useState(0);
  const [servicesTree, setServicesTree] = useState([]);
  const [servicesTreeLoading, setServicesTreeLoading] = useState(false);
  const [servicesTreeError, setServicesTreeError] = useState(null);
  const [expandedServicesNamespaces, setExpandedServicesNamespaces] = useState(new Set());

  // Hub / Instance Monitor integration
  const [hubConfigured, setHubConfigured] = useState(false);
  const [hubMsDialogOpen, setHubMsDialogOpen] = useState(false);
  const [selectedHubInstance, setSelectedHubInstance] = useState(null);

  useEffect(() => {
    fetchInstances();
    fetchTeams();
  }, []);

  // Check whether the instance-monitor Python service has Hub configured
  useEffect(() => {
    getInstanceMonitorHubStatus()
      .then((res) => setHubConfigured(res?.hubUrlSet || false))
      .catch(() => {});
  }, []);

  // Filtered instances for both card and table view
  const filteredInstances = useMemo(() => {
    return instances.filter((instance) => {
      const instanceTypeValue = getInstanceType(instance);
      if (instanceTypeFilter !== 'All' && instanceTypeValue !== instanceTypeFilter) return false;
      if (branchFilter && (instance.cbvData?.branch || '') !== branchFilter) return false;
      if (usageFilter && (instance.usage || '') !== usageFilter) return false;
      if (teamFilter && (instance.team || '') !== teamFilter) return false;
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        if (!(instance.contextName || '').toLowerCase().includes(query)) return false;
      }
      return true;
    });
  }, [instances, instanceTypeFilter, branchFilter, usageFilter, teamFilter, searchQuery]);

  const fetchServicesTree = async () => {
    try {
      setServicesTreeLoading(true);
      setServicesTreeError(null);
      const res = await getInstanceManagementNamespaces();
      const data = (res && res.data) ? res.data : (Array.isArray(res) ? res : []);
      const tree = Array.isArray(data) ? data : [];
      setServicesTree(tree);
      if (tree.length > 0) {
        setExpandedServicesNamespaces(new Set(tree.map(n => String(n._id))));
      }
    } catch (err) {
      setServicesTreeError(err.message || 'Failed to load services');
    } finally {
      setServicesTreeLoading(false);
    }
  };

  useEffect(() => {
    if (servicesTab === 1) fetchServicesTree();
  }, [servicesTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleServicesNamespace = (id) => {
    setExpandedServicesNamespaces(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAllServices = () => {
    setExpandedServicesNamespaces(new Set(servicesTree.map(n => String(n._id))));
  };

  const collapseAllServices = () => {
    setExpandedServicesNamespaces(new Set());
  };

  const fetchTeams = async () => {
    try {
      const teamsData = await getEnabledTeams();
      setTeams(teamsData);
    } catch (err) {
      console.log('Teams fetch failed (non-critical):', err.message || err);
      // Silently fail - teams filter will just be empty
    }
  };

  const fetchInstances = async () => {
    try {
      setLoading(true);
      const response = await getTestInstances();
      const instancesData = response.data || [];
      console.log('Fetched instances:', instancesData);
      instancesData.forEach(instance => {
        console.log(`Instance ${instance.contextName}:`, {
          hasCBV: !!instance.cbvData,
          hasMSV: !!instance.msvData,
          hasAzure: !!instance.azureData,
          cbvData: instance.cbvData,
          msvData: instance.msvData,
          azureData: instance.azureData
        });
      });
      setInstances(instancesData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch test instances');
      console.error('Error fetching test instances:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddInstance = () => {
    setEditingInstance(null);
    setFormData({
      baseURL: '',
      contextName: '',
      usage: '',
      team: '',
      activity: ''
    });
    setOpenDialog(true);
  };

  const handleEditInstance = (instance) => {
    setEditingInstance(instance);
    setFormData({
      baseURL: instance.baseURL,
      contextName: instance.contextName,
      usage: instance.usage || '',
      team: instance.team || '',
      activity: instance.activity || ''
    });
    setOpenDialog(true);
  };

  const handleDeleteInstance = async (id) => {
    if (!window.confirm('Are you sure you want to delete this test instance?')) {
      return;
    }

    try {
      await deleteTestInstance(id);
      setSuccessMessage('Test instance deleted successfully');
      fetchInstances();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to delete test instance');
      console.error('Error deleting test instance:', err);
    }
  };

  const handleSaveInstance = async () => {
    try {
      if (editingInstance) {
        await updateTestInstance(editingInstance._id, formData);
        setSuccessMessage('Test instance updated successfully');
        setOpenDialog(false);
        fetchInstances();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        // Create new instance
        const response = await createTestInstance(formData);
        const newInstanceId = response.data?._id || response.data?.id;
        
        if (newInstanceId) {
          setSuccessMessage('Test instance created successfully. Fetching data...');
          setOpenDialog(false);
          
          // Automatically fetch CBV, MSV, and Azure data for the new instance
          try {
            console.log('Fetching data for newly created instance:', newInstanceId);
            const results = await Promise.allSettled([
              fetchTestInstanceCBV(newInstanceId),
              fetchTestInstanceMSV(newInstanceId),
              fetchTestInstanceAzure(newInstanceId)
            ]);
            
            // Log results
            const apiNames = ['CBV', 'MSV', 'Azure'];
            results.forEach((result, index) => {
              if (result.status === 'rejected') {
                console.error(`${apiNames[index]} fetch failed:`, result.reason);
              } else {
                console.log(`${apiNames[index]} fetch succeeded`);
              }
            });
            
            // Check for errors
            const errors = results.filter(r => r.status === 'rejected');
            if (errors.length > 0) {
              const errorMessages = errors.map((e, i) => {
                return `${apiNames[i]}: ${e.reason?.message || 'Unknown error'}`;
              }).join(', ');
              setError(`Instance created but some data fetch failed: ${errorMessages}`);
            } else {
              setSuccessMessage('Test instance created and all data fetched successfully');
            }
          } catch (fetchErr) {
            console.error('Error fetching data for new instance:', fetchErr);
            setError('Instance created but failed to fetch data: ' + (fetchErr.message || 'Unknown error'));
          }
          
          // Refresh instances to show the new instance with fetched data
          await fetchInstances();
          setTimeout(() => setSuccessMessage(null), 3000);
        } else {
          // Fallback if ID is not returned
          setSuccessMessage('Test instance created successfully');
          setOpenDialog(false);
          fetchInstances();
          setTimeout(() => setSuccessMessage(null), 3000);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to save test instance');
      console.error('Error saving test instance:', err);
    }
  };

  const handleFetchCBV = async (id) => {
    try {
      setFetchingIds(prev => ({ ...prev, [`${id}-cbv`]: true }));
      await fetchTestInstanceCBV(id);
      setSuccessMessage('CBV data fetched successfully');
      fetchInstances();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to fetch CBV data');
      console.error('Error fetching CBV:', err);
    } finally {
      setFetchingIds(prev => ({ ...prev, [`${id}-cbv`]: false }));
    }
  };

  const handleFetchMSV = async (id) => {
    try {
      setFetchingIds(prev => ({ ...prev, [`${id}-msv`]: true }));
      await fetchTestInstanceMSV(id);
      setSuccessMessage('MSV data fetched successfully');
      fetchInstances();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to fetch MSV data');
      console.error('Error fetching MSV:', err);
    } finally {
      setFetchingIds(prev => ({ ...prev, [`${id}-msv`]: false }));
    }
  };

  const handleFetchAzure = async (id) => {
    try {
      setFetchingIds(prev => ({ ...prev, [`${id}-azure`]: true }));
      await fetchTestInstanceAzure(id);
      setSuccessMessage('Azure data fetched successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      // Refresh instances to get updated Azure data
      await fetchInstances();
    } catch (err) {
      setError(err.message || 'Failed to fetch Azure data');
      console.error('Error fetching Azure:', err);
    } finally {
      setFetchingIds(prev => ({ ...prev, [`${id}-azure`]: false }));
    }
  };

  const handleOpenHubMs = (instance) => {
    setSelectedHubInstance(instance);
    setHubMsDialogOpen(true);
  };

  const handleViewAzure = async (instance) => {
    console.log('Opening Azure dialog for instance:', instance.contextName);
    console.log('Instance Azure data:', instance.azureData);
    console.log('Azure data type:', typeof instance.azureData);
    console.log('Azure data keys:', instance.azureData ? Object.keys(instance.azureData) : 'null/undefined');
    
    // Find the latest instance data from state to ensure we have the most up-to-date Azure data
    let latestInstance = instances.find(inst => inst._id === instance._id) || instance;
    
    // If no Azure data found, try refreshing the instance data
    if (!latestInstance.azureData || Object.keys(latestInstance.azureData).length === 0) {
      console.log('No Azure data found in state, refreshing instances...');
      try {
        const response = await getTestInstances();
        const refreshedInstances = response.data || [];
        const refreshedInstance = refreshedInstances.find(inst => inst._id === instance._id);
        if (refreshedInstance) {
          latestInstance = refreshedInstance;
          console.log('Refreshed instance Azure data:', latestInstance.azureData);
          // Update state with refreshed instances
          setInstances(refreshedInstances);
        }
      } catch (err) {
        console.error('Error refreshing instances:', err);
      }
    }
    
    console.log('Latest instance Azure data:', latestInstance.azureData);
    setSelectedInstanceForAzure(latestInstance);
    setAzureDialogOpen(true);
  };

  const handleExportAzure = () => {
    if (!selectedInstanceForAzure || !selectedInstanceForAzure.azureData) {
      return;
    }
    const dataStr = JSON.stringify(selectedInstanceForAzure.azureData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `azure-data-${selectedInstanceForAzure.contextName}-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSyncAll = async () => {
    try {
      setSyncingAll(true);
      console.log('Syncing all instances...');
      
      // Get all instance IDs
      const instanceIds = instances.map(inst => inst._id);
      
      // Call refresh for all instances in parallel
      const results = await Promise.allSettled(
        instanceIds.map(id => 
          Promise.allSettled([
            fetchTestInstanceCBV(id),
            fetchTestInstanceMSV(id),
            fetchTestInstanceAzure(id)
          ])
        )
      );
      
      console.log('Sync all results:', results);
      
      // Check for errors
      const errors = results.filter(r => r.status === 'rejected');
      if (errors.length > 0) {
        const errorMessages = errors.map(e => e.reason?.message || 'Unknown error').join(', ');
        setError(`Some instances failed to sync: ${errorMessages}`);
      } else {
        setSuccessMessage('All instances synced successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
      
      // Refresh instances to get updated data from the server
      await fetchInstances();
      
      // Force a small delay to ensure state updates propagate
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (err) {
      setError(err.message || 'Failed to sync all instances');
      console.error('Error syncing all instances:', err);
      // Still refresh instances even on error to show current state
      await fetchInstances();
    } finally {
      setSyncingAll(false);
    }
  };

  const handleRefreshAll = async (instanceId) => {
    try {
      setFetchingIds(prev => ({ 
        ...prev, 
        [`${instanceId}-refresh`]: true,
        [`${instanceId}-cbv`]: true,
        [`${instanceId}-msv`]: true,
        [`${instanceId}-azure`]: true
      }));
      
      console.log('Refreshing all data for instance:', instanceId);
      
      // Call all 3 APIs in parallel - Promise.allSettled ensures all run even if some fail
      const results = await Promise.allSettled([
        fetchTestInstanceCBV(instanceId),
        fetchTestInstanceMSV(instanceId),
        fetchTestInstanceAzure(instanceId)
      ]);
      
      console.log('Refresh results:', {
        cbv: results[0].status,
        msv: results[1].status,
        azure: results[2].status
      });
      
      // Log any errors
      results.forEach((result, index) => {
        const apiNames = ['CBV', 'MSV', 'Azure'];
        if (result.status === 'rejected') {
          console.error(`${apiNames[index]} fetch failed:`, result.reason);
        } else {
          console.log(`${apiNames[index]} fetch succeeded`);
        }
      });
      
      // Check results
      const errors = results.filter(r => r.status === 'rejected');
      if (errors.length > 0) {
        const errorMessages = errors.map((e, i) => {
          const apiNames = ['CBV', 'MSV', 'Azure'];
          return `${apiNames[i]}: ${e.reason?.message || 'Unknown error'}`;
        }).join(', ');
        setError(`Some APIs failed: ${errorMessages}`);
      } else {
        setSuccessMessage('All data refreshed successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
      
      // Refresh instances to get updated data from the server
      await fetchInstances();
      
      // Clear loading states after refresh
      setFetchingIds(prev => {
        const newState = { ...prev };
        delete newState[`${instanceId}-refresh`];
        delete newState[`${instanceId}-cbv`];
        delete newState[`${instanceId}-msv`];
        delete newState[`${instanceId}-azure`];
        return newState;
      });
    } catch (err) {
      setError(err.message || 'Failed to refresh data');
      console.error('Error refreshing all data:', err);
      // Still refresh instances even on error to show current state
      try {
        await fetchInstances();
      } catch (fetchErr) {
        console.error('Error fetching instances after refresh error:', fetchErr);
      }
    } finally {
      // Clear loading states
      setFetchingIds(prev => {
        const newState = { ...prev };
        delete newState[`${instanceId}-refresh`];
        delete newState[`${instanceId}-cbv`];
        delete newState[`${instanceId}-msv`];
        delete newState[`${instanceId}-azure`];
        return newState;
      });
    }
  };

  const getHealthStatus = (instance) => {
    if (instance.status === 'error') return { color: 'error', icon: <ErrorIcon />, label: 'Error' };
    if (instance.status === 'inactive') return { color: 'warning', icon: <WarningIcon />, label: 'Warning' };
    
    const downServices = instance.getDownServices ? instance.getDownServices() : [];
    if (downServices.length > 0) {
      return { color: 'warning', icon: <WarningIcon />, label: `${downServices.length} Down` };
    }
    
    return { color: 'success', icon: <CheckCircleIcon />, label: 'Good' };
  };

  // Client-side MSV parser - simple and robust
  const parseMSVFromRaw = (rawResponse) => {
    if (!rawResponse || typeof rawResponse !== 'string') return { services: [], servicesDict: {} };
    
    const services = [];
    const servicesDict = {};
    
    // Try to extract text content if it's HTML
    let textContent = rawResponse;
    if (textContent.includes('<')) {
      textContent = textContent.replace(/<[^>]*>/g, '\n').trim();
    }
    
    const lines = textContent.split(/\n|\r/);
    
    lines.forEach((line) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;
      
      // Skip lines that don't contain "Version"
      if (!trimmedLine.match(/Version/i)) {
        return;
      }
      
      // Find "Version" and split the line
      const versionIndex = trimmedLine.toLowerCase().indexOf('version');
      if (versionIndex === -1) return;
      
      // Service name is everything before "Version"
      const serviceName = trimmedLine.substring(0, versionIndex).trim();
      
      // Get the part after "Version" (including "Version" itself)
      const afterVersion = trimmedLine.substring(versionIndex + 'version'.length).trim();
      
      // Split by equals first, if not found then by colon
      // Look for the separator after any whitespace following "Version"
      let versionValue = '';
      let separatorIndex = -1;
      let separator = '';
      
      // Try to find "=" first
      const equalsIndex = afterVersion.indexOf('=');
      if (equalsIndex !== -1) {
        separatorIndex = equalsIndex;
        separator = '=';
      } else {
        // Try to find ":" if "=" not found
        const colonIndex = afterVersion.indexOf(':');
        if (colonIndex !== -1) {
          separatorIndex = colonIndex;
          separator = ':';
        }
      }
      
      if (separatorIndex === -1) {
        // No separator found, skip this line
        return;
      }
      
      // Get everything after the separator
      versionValue = afterVersion.substring(separatorIndex + 1).trim();
      
      if (!serviceName || !versionValue) {
        return;
      }
      
      // Extract bracket contents for additional info
      const bracketMatches = versionValue.match(/\[([^\]]+)\]/g) || [];
      const bracketContents = bracketMatches.map(b => b.replace(/[\[\]]/g, ''));
      
      // Clean version (remove brackets and parentheses for status check)
      let cleanVersion = versionValue.replace(/\[([^\]]+)\]/g, '').trim();
      
      // Extract parentheses content
      const parenthesesMatch = cleanVersion.match(/\(([^)]+)\)/);
      let additionalInfo = bracketContents.join(', ');
      if (parenthesesMatch) {
        additionalInfo = additionalInfo 
          ? `${additionalInfo} | ${parenthesesMatch[1]}`
          : parenthesesMatch[1];
        cleanVersion = cleanVersion.replace(/\(([^)]+)\)/g, '').trim();
      }
      
      // Determine status: if version is "disabled" or contains "not able to connect" (case insensitive)
      const versionLower = cleanVersion.toLowerCase().trim();
      let status = 'connected';
      
      if (versionLower === 'disabled') {
        status = 'disabled';
      } else if (versionLower.includes('not able to connect')) {
        status = 'not_connected';
      }
      
      const serviceData = {
        version: cleanVersion,
        status: status,
        additionalInfo: additionalInfo,
        metadata: {
          brackets: bracketContents,
          rawValue: versionValue
        }
      };
      
      services.push({
        name: serviceName,
        ...serviceData
      });
      
      servicesDict[serviceName] = serviceData;
    });
    
    console.log('parseMSVFromRaw: Parsed', services.length, 'services');
    console.log('parseMSVFromRaw: Services with status:', services.map(s => ({ name: s.name, status: s.status, version: s.version })));
    
    return { services, servicesDict };
  };

  const getDownServices = (instance) => {
    if (!instance.msvData) {
      console.log('getDownServices: No msvData for instance', instance.contextName);
      return [];
    }
    
    // Support both array and dictionary formats
    let services = [];
    
    if (instance.msvData.services && Array.isArray(instance.msvData.services) && instance.msvData.services.length > 0) {
      services = instance.msvData.services;
      console.log('getDownServices: Using services array, count:', services.length);
    } else if (instance.msvData.servicesDict) {
      // Convert dictionary to array if needed
      const servicesDict = instance.msvData.servicesDict instanceof Map
        ? Object.fromEntries(instance.msvData.servicesDict)
        : instance.msvData.servicesDict;
      
      if (servicesDict && typeof servicesDict === 'object' && Object.keys(servicesDict).length > 0) {
        services = Object.entries(servicesDict).map(([name, data]) => ({
          name,
          ...data
        }));
        console.log('getDownServices: Using servicesDict, count:', services.length);
      }
    }
    
    // If no services found, try to parse from rawResponse
    if (services.length === 0 && instance.msvData.rawResponse) {
      console.log('getDownServices: No parsed services, parsing from rawResponse');
      const parsed = parseMSVFromRaw(instance.msvData.rawResponse);
      services = parsed.services;
      console.log('getDownServices: Parsed from rawResponse, count:', services.length);
    }
    
    if (services.length === 0) {
      console.log('getDownServices: No services found');
      return [];
    }
    
    // Log all services and their statuses for debugging
    console.log('getDownServices: All services:', services.map(s => ({ name: s.name, status: s.status, version: s.version })));
    
    const downServices = services.filter(s => {
      if (!s) {
        console.log('getDownServices: Skipping null service');
        return false;
      }
      const status = (s.status || '').toLowerCase().trim();
      const version = (s.version || '').toLowerCase().trim();
      
      console.log(`getDownServices: Checking service "${s.name}" - status: "${status}", version: "${version}"`);
      
      const isDown = status === 'not_connected' || 
                     status === 'disabled' ||
                     status.includes('not able to connect') ||
                     status.includes('not connected') ||
                     version === 'disabled' ||
                     version.includes('not able to connect') ||
                     version.includes('not connected');
      
      if (isDown) {
        console.log(`getDownServices: ✓ Service "${s.name}" is DOWN (status: ${status}, version: ${version})`);
      }
      
      return isDown;
    });
    
    console.log('getDownServices: Down services found:', downServices.length, downServices.map(s => ({ name: s.name, status: s.status })));
    
    return downServices.map(s => ({ name: s.name, status: s.status }));
  };

  const renderCBVTooltip = (instance) => {
    if (!instance) return 'No instance data';
    
    const cbvData = instance.cbvData || {};
    
    // Debug logging
    console.log('CBV Tooltip - Instance:', instance.contextName);
    console.log('CBV Tooltip - Full instance:', instance);
    console.log('CBV Tooltip - CBV Data:', cbvData);
    console.log('CBV Tooltip - Has instanceType:', !!cbvData.instanceType);
    console.log('CBV Tooltip - Has version:', !!cbvData.version);
    console.log('CBV Tooltip - Has memoryUsed:', !!cbvData.memoryUsed);
    
    // Check if we have any data at all
    const hasAnyData = cbvData.instanceType || cbvData.version || cbvData.memoryUsed || 
                       cbvData.ipAddress || cbvData.machineName || cbvData.clusterName;
    
    if (!hasAnyData) {
      return (
        <Box sx={{ p: 1 }}>
          <Typography variant="body2" sx={{ color: 'white' }}>
            No CBV data available. Click refresh icon to fetch.
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ p: 1.5, maxWidth: 350 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'white' }}>
          CBV Data
        </Typography>
        {cbvData.instanceType && (
          <Typography variant="body2" sx={{ mb: 0.5, color: 'white' }}>
            <strong>Type:</strong> {cbvData.instanceType}
          </Typography>
        )}
        {cbvData.version && (
          <Typography variant="body2" sx={{ mb: 0.5, color: 'white' }}>
            <strong>Version:</strong> {cbvData.version}
            {cbvData.commit && ` (${cbvData.commit.substring(0, 7)})`}
          </Typography>
        )}
        {cbvData.memoryUsed !== undefined && cbvData.memoryTotal !== undefined && (
          <Typography variant="body2" sx={{ mb: 0.5, color: 'white' }}>
            <strong>Memory:</strong> {cbvData.memoryUsed} / {cbvData.memoryTotal} MB
            {cbvData.memoryPercentage !== undefined && ` (${cbvData.memoryPercentage}%)`}
          </Typography>
        )}
        {cbvData.percentageUsage !== undefined && (
          <Typography variant="body2" sx={{ mb: 0.5, color: 'white' }}>
            <strong>Usage:</strong> {cbvData.percentageUsage}%
          </Typography>
        )}
        {cbvData.ipAddress && (
          <Typography variant="body2" sx={{ mb: 0.5, color: 'white' }}>
            <strong>IP:</strong> {cbvData.ipAddress}
          </Typography>
        )}
        {cbvData.machineName && (
          <Typography variant="body2" sx={{ mb: 0.5, color: 'white' }}>
            <strong>Machine:</strong> {cbvData.machineName}
          </Typography>
        )}
        {cbvData.clusterName && (
          <Typography variant="body2" sx={{ mb: 0.5, color: 'white' }}>
            <strong>Cluster:</strong> {cbvData.clusterName}
            {cbvData.clusterSize !== undefined && ` (${cbvData.clusterSize} nodes)`}
          </Typography>
        )}
        {cbvData.databaseServer && (
          <Typography variant="body2" sx={{ mb: 0.5, color: 'white' }}>
            <strong>Database:</strong> {cbvData.databaseServer}
            {cbvData.databasePort !== undefined && `:${cbvData.databasePort}`}
          </Typography>
        )}
        {cbvData.lastFetched && (
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
            Last fetched: {new Date(cbvData.lastFetched).toLocaleString()}
          </Typography>
        )}
      </Box>
    );
  };

  const renderMSVTooltip = (instance) => {
    if (!instance) {
      return (
        <Box sx={{ p: 1 }}>
          <Typography variant="body2" sx={{ color: 'white' }}>
            No instance data
          </Typography>
        </Box>
      );
    }
    
    const msvData = instance.msvData || {};
    
    // Debug logging
    console.log('MSV Tooltip - Instance:', instance.contextName);
    console.log('MSV Tooltip - Full MSV Data:', JSON.stringify(msvData, null, 2));
    console.log('MSV Tooltip - Services:', msvData.services);
    console.log('MSV Tooltip - Services type:', typeof msvData.services, Array.isArray(msvData.services));
    console.log('MSV Tooltip - Services Dict:', msvData.servicesDict);
    console.log('MSV Tooltip - Services Dict type:', typeof msvData.servicesDict);
    console.log('MSV Tooltip - Services length:', msvData.services?.length);
    console.log('MSV Tooltip - Statistics:', msvData.statistics);
    
    // Support both array and dictionary formats
    let services = [];
    let servicesDict = {};
    
    // Check for array format
    if (msvData.services) {
      if (Array.isArray(msvData.services) && msvData.services.length > 0) {
        services = msvData.services;
        console.log('MSV Tooltip - Using services array, count:', services.length);
      }
    }
    
    // Handle dictionary format (Map from MongoDB or object from API)
    if (msvData.servicesDict) {
      try {
        if (msvData.servicesDict instanceof Map) {
          servicesDict = Object.fromEntries(msvData.servicesDict);
          console.log('MSV Tooltip - Converted Map to object, keys:', Object.keys(servicesDict).length);
        } else if (typeof msvData.servicesDict === 'object' && msvData.servicesDict !== null) {
          servicesDict = msvData.servicesDict;
          console.log('MSV Tooltip - Using servicesDict object, keys:', Object.keys(servicesDict).length);
        }
        
        // If we have dictionary but no array, convert dictionary to array
        if (services.length === 0 && Object.keys(servicesDict).length > 0) {
          services = Object.entries(servicesDict).map(([name, data]) => ({
            name,
            version: data?.version || 'N/A',
            status: data?.status || 'unknown',
            additionalInfo: data?.additionalInfo || '',
            ...data
          }));
          console.log('MSV Tooltip - Converted dict to array, count:', services.length);
        }
      } catch (error) {
        console.error('MSV Tooltip - Error processing servicesDict:', error);
      }
    }
    
    // Check if we have any data at all
    const hasData = services.length > 0 || (servicesDict && Object.keys(servicesDict).length > 0);
    console.log('MSV Tooltip - Has data:', hasData, 'Services count:', services.length, 'Dict keys:', servicesDict ? Object.keys(servicesDict).length : 0);
    
    if (!hasData) {
      return (
        <Box sx={{ p: 1 }}>
          <Typography variant="body2" sx={{ color: 'white' }}>
            No MSV data available. Click refresh icon to fetch.
          </Typography>
        </Box>
      );
    }
    
    const stats = msvData.statistics || {};
    const downServices = services.filter(s => 
      s && (s.status === 'not_connected' || s.status === 'disabled')
    );
    
    // Calculate stats if not provided
    const totalServices = services.length || (servicesDict ? Object.keys(servicesDict).length : 0);
    const connectedCount = stats.connected !== undefined ? stats.connected : services.filter(s => s && s.status === 'connected').length;
    const disabledCount = stats.disabled !== undefined ? stats.disabled : services.filter(s => s && s.status === 'disabled').length;
    const notConnectedCount = stats.notConnected !== undefined ? stats.notConnected : services.filter(s => s && s.status === 'not_connected').length;
    
    console.log('MSV Tooltip - Rendering with:', {
      totalServices,
      connectedCount,
      disabledCount,
      notConnectedCount,
      downServicesCount: downServices.length
    });
    
    return (
      <Box sx={{ p: 1.5, maxWidth: 450 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'white' }}>
          MSV Data
        </Typography>
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.5, color: 'white' }}>
            <strong>Total Services:</strong> {totalServices}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
            {connectedCount > 0 && (
              <Chip label={`${connectedCount} Connected`} color="success" size="small" />
            )}
            {disabledCount > 0 && (
              <Chip label={`${disabledCount} Disabled`} color="warning" size="small" />
            )}
            {notConnectedCount > 0 && (
              <Chip label={`${notConnectedCount} Not Connected`} color="error" size="small" />
            )}
          </Box>
        </Box>
        {downServices.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#ffcdd2' }}>
              Down Services ({downServices.length}):
            </Typography>
            {downServices.slice(0, 5).map((service, idx) => (
              <Typography key={idx} variant="body2" sx={{ fontSize: '0.85rem', mb: 0.25, color: 'white' }}>
                • {service.name} - {service.version || 'N/A'}
              </Typography>
            ))}
            {downServices.length > 5 && (
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                +{downServices.length - 5} more
              </Typography>
            )}
          </Box>
        )}
        {services.length > 0 && downServices.length === 0 && (
          <Typography variant="body2" sx={{ color: 'white', mt: 1 }}>
            All services are connected ({totalServices} total)
          </Typography>
        )}
        {msvData.lastFetched && (
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
            Last fetched: {new Date(msvData.lastFetched).toLocaleString()}
          </Typography>
        )}
      </Box>
    );
  };

  const handleViewMSV = (instance) => {
    setSelectedInstanceForMsv(instance);
    setMsvDialogOpen(true);
  };

  const handleViewCBV = (instance) => {
    setSelectedInstanceForCbv(instance);
    setCbvDialogOpen(true);
  };

  const handleExportMSV = (instance) => {
    if (!instance.msvData) return;
    
    const dataToExport = {
      instance: {
        baseURL: instance.baseURL,
        contextName: instance.contextName
      },
      msvData: instance.msvData,
      exportedAt: new Date().toISOString()
    };
    
    const jsonStr = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `msv-data-${instance.contextName}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportCBV = (instance) => {
    if (!instance.cbvData) return;
    
    const dataToExport = {
      instance: {
        baseURL: instance.baseURL,
        contextName: instance.contextName
      },
      cbvData: instance.cbvData,
      exportedAt: new Date().toISOString()
    };
    
    const jsonStr = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cbv-data-${instance.contextName}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  // ── instance-monitor–style table view ──────────────────────────────────────
  // Slate design tokens (Tailwind equivalents as hex)
  const SL = {
    bg:       '#ffffff',
    bgPage:   '#f8fafc',   // slate-50
    bgHover:  '#f8fafc',
    bgExpand: 'rgba(239,246,255,0.4)', // blue-50/40
    border:   '#e2e8f0',   // slate-200
    border2:  '#f1f5f9',   // slate-100
    th:       '#64748b',   // slate-500
    name:     '#0f172a',   // slate-900
    muted:    '#94a3b8',   // slate-400
    mono:     '#475569',   // slate-600
    monoBg:   '#f1f5f9',   // slate-100
    blue:     '#2563eb',   // blue-600
    blueHov:  '#1d4ed8',
  };

  // Format upTime to "MMM D, HH:mm".
  // 1. If the raw string contains a full datetime (e.g. "2026.03.12 14:21:38 ...") use it.
  // 2. Otherwise back-calculate server start time from lastFetched - upTimeSeconds.
  // 3. Fall back to HH:MM from the raw string if nothing else works.
  const formatUpTime = (raw, cbvData = {}) => {
    if (!raw) return '—';
    const fmt = (d) => d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
    // Try embedded datetime first
    const m = raw.match(/(\d{4})[.\-/](\d{2})[.\-/](\d{2})\s+(\d{2}):(\d{2})/);
    if (m) return fmt(new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]));
    // Back-calculate from lastFetched - upTimeSeconds
    if (cbvData.upTimeSeconds && cbvData.lastFetched) {
      const start = new Date(new Date(cbvData.lastFetched).getTime() - cbvData.upTimeSeconds * 1000);
      return fmt(start);
    }
    // Fall back to HH:MM from raw
    const t = raw.match(/(\d{1,2}):(\d{2})/);
    if (t) return `${t[1].padStart(2,'0')}:${t[2]}`;
    return raw;
  };

  // Tag colour palette (pastel, cycled by hash)
  const TAG_PALETTES = [
    { bg: '#dbeafe', text: '#1d4ed8' },  // blue
    { bg: '#f3e8ff', text: '#7e22ce' },  // purple
    { bg: '#dcfce7', text: '#15803d' },  // green
    { bg: '#ffedd5', text: '#c2410c' },  // orange
    { bg: '#fce7f3', text: '#be185d' },  // pink
    { bg: '#ccfbf1', text: '#0f766e' },  // teal
    { bg: '#e0e7ff', text: '#4338ca' },  // indigo
    { bg: '#fef9c3', text: '#a16207' },  // amber
  ];
  const tagColour = (str) => {
    let h = 0;
    for (let i = 0; i < (str || '').length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffffffff;
    return TAG_PALETTES[Math.abs(h) % TAG_PALETTES.length];
  };

  // instance-monitor–style type badge
  const TypeBadge = ({ type }) => {
    const colors = type === 'Modern'
      ? { bg: '#dbeafe', text: '#1d4ed8' }
      : type === 'Legacy'
      ? { bg: '#fef9c3', text: '#a16207' }
      : { bg: '#f1f5f9', text: '#64748b' };
    return (
      <Box component="span" sx={{
        display: 'inline-block', borderRadius: '4px', px: '6px', py: '1px',
        bgcolor: colors.bg, color: colors.text, fontSize: '0.68rem', fontWeight: 700,
      }}>
        {type}
      </Box>
    );
  };

  const renderTableView = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
          <CircularProgress size={32} sx={{ color: SL.blue }} />
        </Box>
      );
    }

    const hasActiveFilters = searchQuery.trim() || instanceTypeFilter !== 'All' || branchFilter || usageFilter || teamFilter;
    if (filteredInstances.length === 0) {
      return (
        <Box sx={{ p: 6, textAlign: 'center', bgcolor: SL.bg, borderRadius: '12px', border: `1px solid ${SL.border}` }}>
          <Typography sx={{ color: SL.muted, fontSize: '0.875rem' }}>
            {hasActiveFilters
              ? 'No instances match the current filters.'
              : 'No test instances found.' + (canPerformQAActions() ? ' Click "Add Instance" to create one.' : '')}
          </Typography>
        </Box>
      );
    }

    // columns: chevron | Instance | Version | Build Time | Branch | Status | Refresh
    const COL_SPAN = 7;

    // Shared cell sx
    const thSx = {
      fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '0.06em', color: SL.th, bgcolor: SL.bg,
      borderBottom: `1px solid ${SL.border}`, py: '10px', px: '14px',
      whiteSpace: 'nowrap',
    };
    const tdSx = { px: '14px', py: '12px', borderBottom: `1px solid ${SL.border2}`, verticalAlign: 'top' };

    return (
      <Box sx={{ borderRadius: '12px', border: `1px solid ${SL.border}`, overflow: 'hidden', bgcolor: SL.bg }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
          {/* ── thead ── */}
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={{ ...thSx, width: 36, px: '10px' }} />  {/* chevron */}
              <Box component="th" sx={{ ...thSx }}>Instance</Box>
              <Box component="th" sx={{ ...thSx }}>Version</Box>
              <Box component="th" sx={{ ...thSx }}>Up Time</Box>
              <Box component="th" sx={{ ...thSx }}>Branch</Box>
              <Box component="th" sx={{ ...thSx }}>Status</Box>
              <Box component="th" sx={{ ...thSx, textAlign: 'right', width: 80 }} />  {/* Refresh */}
            </Box>
          </Box>

          {/* ── tbody ── */}
          <Box component="tbody">
            {filteredInstances.map((instance) => {
              const cbvData     = instance.cbvData || {};
              const isExpanded  = expandedCards[instance._id] || false;
              const isFetching  = fetchingIds[`${instance._id}-refresh`];
              const instanceType = getInstanceType(instance);
              const teamLabel   = instance.team ? (teams.find(t => t.id === instance.team)?.displayName || instance.team) : null;
              const metaChips   = [
                instance.label && { key: 'label', value: instance.label },
                teamLabel      && { key: 'team',  value: teamLabel },
                instance.notes && { key: 'notes', value: instance.notes.length > 40 ? instance.notes.slice(0, 40) + '…' : instance.notes, title: instance.notes },
              ].filter(Boolean);
              const labels      = [instance.usage].filter(Boolean);
              const instanceStatus = instance.status === 'active' ? 'green' : instance.status === 'error' ? 'red' : 'N/A';

              const buildTime = formatUpTime(cbvData.upTime, cbvData);

              const rowBg = isExpanded ? SL.bgExpand : 'transparent';

              return (
                <React.Fragment key={instance._id}>
                  {/* ── main row ── */}
                  <Box
                    component="tr"
                    onClick={() => setExpandedCards(prev => ({ ...prev, [instance._id]: !prev[instance._id] }))}
                    sx={{
                      bgcolor: rowBg,
                      cursor: 'pointer',
                      transition: 'background-color 0.15s',
                      '&:hover': { bgcolor: isExpanded ? SL.bgExpand : SL.bgHover },
                    }}
                  >
                    {/* chevron */}
                    <Box component="td" sx={{ ...tdSx, width: 36, px: '10px', color: SL.muted }}>
                      <Box sx={{ fontSize: '0.9rem', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'flex' }}>
                        ▼
                      </Box>
                    </Box>

                    {/* Instance name + URL + tags */}
                    <Box component="td" sx={{ ...tdSx, minWidth: 200 }}
                      onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Box
                              component="a"
                              href={instance.baseURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                fontWeight: 600, fontSize: '0.875rem', color: SL.name,
                                textDecoration: 'none', '&:hover': { color: SL.blue },
                              }}
                            >
                              {instance.contextName}
                            </Box>
                            <Box component="span" sx={{ fontSize: '0.7rem', color: SL.muted }}>↗</Box>
                          </Box>
                          <Box sx={{ fontSize: '0.7rem', color: SL.muted, mt: '2px',
                            maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={instance.baseURL}>
                            {instance.baseURL}
                          </Box>
                          {(labels.length > 0 || metaChips.length > 0 || instanceType !== 'Unknown') && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mt: '6px' }}>
                              {/* Instance type chip — always yellow */}
                              {instanceType !== 'Unknown' && (
                                <Box component="span" sx={{
                                  bgcolor: '#fef9c3', color: '#a16207',
                                  borderRadius: '9999px',
                                  px: '8px', py: '1px', fontSize: '0.68rem', fontWeight: 700,
                                }}>
                                  {instanceType}
                                </Box>
                              )}
                              {labels.map((lbl) => {
                                const c = tagColour(lbl);
                                return (
                                  <Box key={lbl} component="span" sx={{
                                    bgcolor: c.bg, color: c.text, borderRadius: '9999px',
                                    px: '8px', py: '1px', fontSize: '0.68rem', fontWeight: 500,
                                  }}>
                                    {lbl}
                                  </Box>
                                );
                              })}
                              {metaChips.map(({ key, value, title }) => {
                                const c = tagColour(value);
                                return (
                                  <Box key={key} component="span" title={title || value} sx={{
                                    bgcolor: c.bg, color: c.text, borderRadius: '9999px',
                                    px: '8px', py: '1px', fontSize: '0.68rem', fontWeight: 500,
                                    maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap', display: 'inline-block',
                                  }}>
                                    {value}
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Version */}
                    <Box component="td" sx={{ ...tdSx }}>
                      {cbvData.version ? (
                        <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', bgcolor: SL.monoBg,
                          color: SL.mono, px: '6px', py: '2px', borderRadius: '4px', display: 'inline-block' }}>
                          {cbvData.version}{cbvData.commit ? ` (${cbvData.commit.slice(0,7)})` : ''}
                        </Box>
                      ) : <Box sx={{ color: SL.muted, fontSize: '0.82rem' }}>—</Box>}
                    </Box>

                    {/* Build Time */}
                    <Box component="td" sx={{ ...tdSx }}>
                      <Box sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: SL.mono }}>
                        {buildTime}
                      </Box>
                    </Box>

                    {/* Branch */}
                    <Box component="td" sx={{ ...tdSx }}>
                      {cbvData.branch ? (
                        <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', bgcolor: SL.monoBg,
                          color: SL.mono, px: '6px', py: '2px', borderRadius: '4px', display: 'inline-block',
                          maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={cbvData.branch}>
                          {cbvData.branch}
                        </Box>
                      ) : <Box sx={{ color: SL.muted, fontSize: '0.82rem' }}>—</Box>}
                    </Box>

                    {/* Status */}
                    <Box component="td" sx={{ ...tdSx }} onClick={(e) => e.stopPropagation()}>
                      <HubStatusBadge status={instanceStatus} variant="badge" />
                    </Box>

                    {/* Refresh */}
                    <Box component="td" sx={{ ...tdSx, textAlign: 'right', pr: '16px' }}
                      onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={isFetching ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
                        onClick={() => handleRefreshAll(instance._id)}
                        disabled={!!isFetching}
                        sx={{
                          fontSize: '0.72rem', fontWeight: 500, textTransform: 'none',
                          color: SL.th, borderColor: SL.border, px: '10px', py: '3px',
                          '&:hover': { borderColor: '#cbd5e1', color: SL.name, bgcolor: SL.bgHover },
                        }}
                      >
                        Refresh
                      </Button>
                    </Box>
                  </Box>

                  {/* ── expanded row ── */}
                  {isExpanded && (
                    <Box component="tr">
                      <Box component="td" colSpan={COL_SPAN} sx={{ p: 0, border: 'none' }}>
                        <InstanceExpandedTabs
                          instance={instance}
                          hubConfigured={hubConfigured}
                          onSaveMeta={async (fields) => {
                            await updateTestInstance(instance._id, fields);
                            setInstances(prev => prev.map(i =>
                              i._id === instance._id ? { ...i, ...fields } : i
                            ));
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </Box>

        {/* Pagination info */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderTop: `1px solid ${SL.border}`, px: '20px', py: '10px', bgcolor: SL.bg }}>
          <Typography sx={{ fontSize: '0.78rem', color: SL.muted }}>
            {filteredInstances.length} instance{filteredInstances.length !== 1 ? 's' : ''}
            {filteredInstances.length !== instances.length ? ` (filtered from ${instances.length})` : ''}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={servicesTab} onChange={(e, v) => setServicesTab(v)}>
          <Tab label="Instances" />
          <Tab label="Services" />
        </Tabs>
      </Box>

      {servicesTab === 0 && (
      <>
      {/* ── Page header ─────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
            Test Instances
          </Typography>
          <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mt: '2px' }}>
            Manage and monitor test instances
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={syncingAll ? <CircularProgress size={13} /> : <RefreshIcon sx={{ fontSize: 15 }} />}
            onClick={handleSyncAll}
            disabled={syncingAll || instances.length === 0}
            sx={{
              fontSize: '0.78rem', fontWeight: 500, textTransform: 'none',
              color: '#475569', borderColor: '#e2e8f0', px: '12px',
              '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' },
            }}
          >
            Sync All
          </Button>
          {canPerformQAActions() && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 15 }} />}
              onClick={handleAddInstance}
              sx={{
                fontSize: '0.78rem', fontWeight: 600, textTransform: 'none',
                bgcolor: '#2563eb', px: '14px',
                '&:hover': { bgcolor: '#1d4ed8' },
              }}
            >
              Add Instance
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Filter bar ──────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
        bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px',
        px: '14px', py: '10px', mb: 2,
      }}>
        {/* Search */}
        <Box sx={{
          flex: '1 1 200px', display: 'flex', alignItems: 'center', gap: '6px',
          bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '7px',
          px: '10px', py: '6px', minWidth: 180,
        }}>
          <SearchIcon sx={{ fontSize: 15, color: '#94a3b8', flexShrink: 0 }} />
          <Box
            component="input"
            placeholder="Search instances..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 1, border: 'none', outline: 'none', background: 'transparent',
              fontSize: '0.82rem', color: '#0f172a',
              '&::placeholder': { color: '#94a3b8' },
            }}
          />
        </Box>

        {/* Type */}
        <Box
          component="select"
          value={instanceTypeFilter}
          onChange={(e) => setInstanceTypeFilter(e.target.value)}
          sx={{
            border: '1px solid #e2e8f0', borderRadius: '7px', bgcolor: '#f8fafc',
            px: '10px', py: '7px', fontSize: '0.8rem', color: instanceTypeFilter !== 'All' ? '#0f172a' : '#64748b',
            outline: 'none', cursor: 'pointer', minWidth: 120, appearance: 'auto',
            '&:focus': { borderColor: '#2563eb' },
          }}
        >
          <option value="All">All types</option>
          <option value="Modern">Modern</option>
          <option value="Legacy">Legacy</option>
        </Box>

        {/* Branch */}
        <Box
          component="select"
          value={branchFilter}
          onChange={(e) => setBranchFilter(e.target.value)}
          sx={{
            border: '1px solid #e2e8f0', borderRadius: '7px', bgcolor: '#f8fafc',
            px: '10px', py: '7px', fontSize: '0.8rem', color: branchFilter ? '#0f172a' : '#64748b',
            outline: 'none', cursor: 'pointer', minWidth: 130, appearance: 'auto',
            '&:focus': { borderColor: '#2563eb' },
          }}
        >
          <option value="">All branches</option>
          {[...new Set(instances
            .map(inst => inst.cbvData?.branch)
            .filter(branch => branch && branch.trim())
          )].sort().map((branch) => (
            <option key={branch} value={branch}>{branch}</option>
          ))}
        </Box>

        {/* Usage */}
        <Box
          component="select"
          value={usageFilter}
          onChange={(e) => setUsageFilter(e.target.value)}
          sx={{
            border: '1px solid #e2e8f0', borderRadius: '7px', bgcolor: '#f8fafc',
            px: '10px', py: '7px', fontSize: '0.8rem', color: usageFilter ? '#0f172a' : '#64748b',
            outline: 'none', cursor: 'pointer', minWidth: 130, appearance: 'auto',
            '&:focus': { borderColor: '#2563eb' },
          }}
        >
          <option value="">All usage</option>
          <option value="Functional Testing">Functional Testing</option>
          <option value="Automation">Automation</option>
          <option value="PM">PM</option>
          <option value="Dev Testing">Dev Testing</option>
        </Box>

        {/* Team */}
        <Box
          component="select"
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
          sx={{
            border: '1px solid #e2e8f0', borderRadius: '7px', bgcolor: '#f8fafc',
            px: '10px', py: '7px', fontSize: '0.8rem', color: teamFilter ? '#0f172a' : '#64748b',
            outline: 'none', cursor: 'pointer', minWidth: 120, appearance: 'auto',
            '&:focus': { borderColor: '#2563eb' },
          }}
        >
          <option value="">All teams</option>
          {teams.map((team) => (
            <option key={team.id} value={team.id}>{team.displayName || team.id}</option>
          ))}
        </Box>

        {/* Clear filters */}
        {(searchQuery || instanceTypeFilter !== 'All' || branchFilter || usageFilter || teamFilter) && (
          <Button
            size="small"
            onClick={() => { setSearchQuery(''); setInstanceTypeFilter('All'); setBranchFilter(''); setUsageFilter(''); setTeamFilter(''); }}
            sx={{
              fontSize: '0.75rem', fontWeight: 500, textTransform: 'none', color: '#64748b',
              px: '10px', py: '5px', minWidth: 0, borderRadius: '7px',
              '&:hover': { bgcolor: '#f1f5f9', color: '#0f172a' },
            }}
          >
            Clear
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {renderTableView()}

      {/* Azure Data Dialog */}
      <Dialog 
        open={azureDialogOpen} 
        onClose={() => setAzureDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Azure Data - {selectedInstanceForAzure?.contextName}
            </Typography>
            <IconButton onClick={() => setAzureDialogOpen(false)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {(() => {
            console.log('Dialog - selectedInstanceForAzure:', selectedInstanceForAzure);
            console.log('Dialog - azureData:', selectedInstanceForAzure?.azureData);
            const azureData = selectedInstanceForAzure?.azureData;
            
            // Check if azureData exists and has content
            if (!azureData) {
              console.log('No azureData found');
              return (
                <Typography variant="body2" color="textSecondary">
                  No Azure data available. Click the refresh button to fetch Azure data.
                </Typography>
              );
            }
            
            // Handle different data types
            let dataEntries = [];
            if (typeof azureData === 'object' && azureData !== null) {
              if (Array.isArray(azureData)) {
                console.log('Azure data is an array');
                return (
                  <Typography variant="body2" color="textSecondary">
                    Azure data is in an unexpected format (array). Please check the console for details.
                  </Typography>
                );
              } else {
                dataEntries = Object.entries(azureData);
                console.log('Azure data entries:', dataEntries);
              }
            } else {
              console.log('Azure data is not an object:', typeof azureData);
              return (
                <Typography variant="body2" color="textSecondary">
                  Azure data is in an unexpected format. Please check the console for details.
                </Typography>
              );
            }
            
            if (dataEntries.length === 0) {
              console.log('Azure data object is empty');
              return (
                <Typography variant="body2" color="textSecondary">
                  No Azure data available. Click the refresh button to fetch Azure data.
                </Typography>
              );
            }
            
            return (
              <Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Key</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dataEntries.map(([key, value]) => (
                        <TableRow key={key}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {key}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {String(value)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAzureDialogOpen(false)}>Close</Button>
          {selectedInstanceForAzure?.azureData && Object.keys(selectedInstanceForAzure.azureData).length > 0 && (
            <Button onClick={handleExportAzure} startIcon={<DownloadIcon />} variant="outlined">
              Export JSON
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Hub Microservices Dialog */}
      <Dialog
        open={hubMsDialogOpen}
        onClose={() => setHubMsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Hub Microservices — {selectedHubInstance?.contextName}
            </Typography>
            <IconButton onClick={() => setHubMsDialogOpen(false)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1 }}>
          {selectedHubInstance && (
            <HubMicroservicePanel
              instanceName={selectedHubInstance.contextName}
              baseURL={selectedHubInstance.baseURL}
              team={selectedHubInstance.team}
              hubConfigured={hubConfigured}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHubMsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* MSV Data Dialog */}
      <Dialog
        open={msvDialogOpen}
        onClose={() => setMsvDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              MSV Data - {selectedInstanceForMsv?.contextName}
            </Typography>
            {selectedInstanceForMsv?.msvData && (
              <Button
                startIcon={<DownloadIcon />}
                onClick={() => handleExportMSV(selectedInstanceForMsv)}
                variant="outlined"
                size="small"
              >
                Export JSON
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedInstanceForMsv?.msvData ? (() => {
            // Support both array and dictionary formats
            let services = [];
            let servicesDict = {};
            const msvData = selectedInstanceForMsv.msvData;
            
            // First try to use existing parsed data
            if (msvData.services && Array.isArray(msvData.services) && msvData.services.length > 0) {
              services = msvData.services;
            } else if (msvData.servicesDict) {
              // Convert dictionary to array
              const dict = msvData.servicesDict instanceof Map
                ? Object.fromEntries(msvData.servicesDict)
                : msvData.servicesDict;
              
              if (dict && typeof dict === 'object' && Object.keys(dict).length > 0) {
                servicesDict = dict;
                services = Object.entries(dict).map(([name, data]) => ({
                  name,
                  version: data?.version || 'N/A',
                  status: data?.status || 'unknown',
                  additionalInfo: data?.additionalInfo || '',
                  ...data
                }));
              }
            }
            
            // If no services found, try to parse from rawResponse
            if (services.length === 0 && msvData.rawResponse) {
              console.log('No parsed services found, attempting to parse rawResponse');
              const parsed = parseMSVFromRaw(msvData.rawResponse);
              services = parsed.services;
              servicesDict = parsed.servicesDict;
              console.log('Parsed from rawResponse:', services.length, 'services');
            }
            
            if (services.length === 0) {
              return (
                <Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    No MSV data available. Click the refresh icon to fetch data.
                  </Typography>
                  {msvData.rawResponse && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Raw Response (for debugging):
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 300, overflow: 'auto' }}>
                        <Typography variant="body2" component="pre" sx={{ 
                          fontFamily: 'monospace', 
                          fontSize: '0.75rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {msvData.rawResponse}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </Box>
              );
            }
            
            // Calculate statistics if not available
            const stats = msvData.statistics || {};
            const totalServices = services.length;
            const connectedCount = stats.connected !== undefined ? stats.connected : services.filter(s => s && s.status === 'connected').length;
            const disabledCount = stats.disabled !== undefined ? stats.disabled : services.filter(s => s && s.status === 'disabled').length;
            const notConnectedCount = stats.notConnected !== undefined ? stats.notConnected : services.filter(s => s && s.status === 'not_connected').length;
            
            return (
              <Box>
                {/* Statistics */}
                <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`Total: ${totalServices}`} 
                    color="default"
                  />
                  <Chip 
                    label={`Connected: ${connectedCount}`} 
                    color="success"
                  />
                  <Chip 
                    label={`Disabled: ${disabledCount}`} 
                    color="warning"
                  />
                  <Chip 
                    label={`Not Connected: ${notConnectedCount}`} 
                    color="error"
                  />
                </Box>
                
                {/* Services Table */}
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Service Name</strong></TableCell>
                        <TableCell><strong>Version</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Additional Info</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {services.map((service, idx) => (
                        <TableRow 
                          key={idx}
                          sx={{
                            bgcolor: service.status === 'not_connected' || service.status === 'disabled' 
                              ? 'error.light' 
                              : 'transparent'
                          }}
                        >
                          <TableCell><strong>{service.name}</strong></TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily: 'monospace',
                                fontSize: '0.85rem'
                              }}
                            >
                              {service.version || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={service.status || 'unknown'}
                              color={
                                service.status === 'connected' ? 'success' :
                                service.status === 'disabled' ? 'warning' :
                                service.status === 'not_connected' ? 'error' : 'default'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              {service.additionalInfo || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Last Fetched */}
                {msvData.lastFetched && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                    Last fetched: {new Date(msvData.lastFetched).toLocaleString()}
                  </Typography>
                )}
              </Box>
            );
          })() : (
            <Typography variant="body2" color="textSecondary">
              No MSV data available. Click the refresh icon to fetch data.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMsvDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* CBV Data Dialog */}
      <Dialog 
        open={cbvDialogOpen} 
        onClose={() => setCbvDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              CBV Data - {selectedInstanceForCbv?.contextName}
            </Typography>
            {selectedInstanceForCbv?.cbvData && (
              <Button
                startIcon={<DownloadIcon />}
                onClick={() => handleExportCBV(selectedInstanceForCbv)}
                variant="outlined"
                size="small"
              >
                Export JSON
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedInstanceForCbv?.cbvData ? (() => {
            const cbvData = selectedInstanceForCbv.cbvData;
            
            // Check for any data - expand the check to include more fields
            const hasAnyData = cbvData.instanceType || cbvData.version || cbvData.memoryUsed !== undefined || 
                               cbvData.ipAddress || cbvData.machineName || cbvData.clusterName ||
                               cbvData.currentInstanceIp || cbvData.upTime || cbvData.activeSessions !== undefined ||
                               cbvData.currentDbConnections !== undefined || cbvData.instanceUseType ||
                               cbvData.instanceCategory || cbvData.infrastructureGrade || cbvData.percentageUsage !== undefined;
            
            console.log('CBV Dialog - cbvData:', cbvData);
            console.log('CBV Dialog - hasAnyData:', hasAnyData);
            
            if (!hasAnyData) {
              return (
                <Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    No CBV data available. Click the refresh icon to fetch data.
                  </Typography>
                  {/* Debug: Show raw data structure */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Debug - Raw CBV Data:
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 300, overflow: 'auto' }}>
                      <Typography variant="body2" component="pre" sx={{ 
                        fontFamily: 'monospace', 
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {JSON.stringify(cbvData, null, 2)}
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
              );
            }
            
            return (
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                  {cbvData.instanceType && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Instance Type
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cbvData.instanceType}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.version && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Version
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                        {cbvData.version}
                        {cbvData.commit && ` (${cbvData.commit.substring(0, 7)})`}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.memoryUsed !== undefined && cbvData.memoryTotal !== undefined && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Memory
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cbvData.memoryUsed} / {cbvData.memoryTotal} MB
                        {cbvData.memoryPercentage !== undefined && ` (${cbvData.memoryPercentage}%)`}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.percentageUsage !== undefined && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Usage
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cbvData.percentageUsage}%
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.ipAddress && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        IP Address
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                        {cbvData.ipAddress}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.machineName && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Machine Name
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cbvData.machineName}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.clusterName && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Cluster
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cbvData.clusterName}
                        {cbvData.clusterSize !== undefined && ` (${cbvData.clusterSize} nodes)`}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.databaseServer && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Database Server
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                        {cbvData.databaseServer}
                        {cbvData.databasePort !== undefined && `:${cbvData.databasePort}`}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.instanceUseType && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Instance Use Type
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cbvData.instanceUseType}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.instanceCategory && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Instance Category
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cbvData.instanceCategory}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.infrastructureGrade && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Infrastructure Grade
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cbvData.infrastructureGrade}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.currentInstanceIp && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Current Instance IP
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                        {cbvData.currentInstanceIp}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.upTime && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Uptime
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cbvData.upTime}
                        {cbvData.upTimeSeconds !== undefined && ` (${cbvData.upTimeSeconds} seconds)`}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.configurationType && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Configuration Type
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cbvData.configurationType}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.workflowMessageBroker && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Workflow Message Broker
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cbvData.workflowMessageBroker}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.activeSessions !== undefined && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Active Sessions
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cbvData.activeSessions}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.currentDbConnections !== undefined && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        DB Connections
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cbvData.currentDbConnections}
                        {cbvData.maxDbConnections !== undefined && ` / ${cbvData.maxDbConnections} max`}
                      </Typography>
                    </Grid>
                  )}
                  {cbvData.totalThreads !== undefined && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Threads
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {cbvData.activeThreads !== undefined ? `${cbvData.activeThreads} / ${cbvData.totalThreads}` : cbvData.totalThreads}
                        {cbvData.percentageBusy !== undefined && ` (${cbvData.percentageBusy}% busy)`}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                {cbvData.lastFetched && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 3, display: 'block' }}>
                    Last fetched: {new Date(cbvData.lastFetched).toLocaleString()}
                  </Typography>
                )}
              </Box>
            );
          })() : (
            <Typography variant="body2" color="textSecondary">
              No CBV data available. Click the refresh icon to fetch data.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCbvDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingInstance ? 'Edit Test Instance' : 'Add Test Instance'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Base URL"
              value={formData.baseURL}
              onChange={(e) => setFormData({ ...formData, baseURL: e.target.value })}
              margin="normal"
              placeholder="https://example.com"
              required
            />
            <TextField
              fullWidth
              label="Context Name"
              value={formData.contextName}
              onChange={(e) => setFormData({ ...formData, contextName: e.target.value })}
              margin="normal"
              placeholder="contextname"
              required
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Usage</InputLabel>
              <Select
                value={formData.usage}
                label="Usage"
                onChange={(e) => setFormData({ ...formData, usage: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="Functional Testing">Functional Testing</MenuItem>
                <MenuItem value="Automation">Automation</MenuItem>
                <MenuItem value="PM">PM</MenuItem>
                <MenuItem value="Dev Testing">Dev Testing</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Team</InputLabel>
              <Select
                value={formData.team || ''}
                label="Team"
                onChange={(e) => setFormData({ ...formData, team: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {teams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.displayName || team.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Activity"
              value={formData.activity || ''}
              onChange={(e) => setFormData({ ...formData, activity: e.target.value })}
              margin="normal"
              placeholder="e.g. Regression, Smoke tests"
              multiline
              minRows={1}
              maxRows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveInstance}
            variant="contained"
            disabled={!formData.baseURL || !formData.contextName}
          >
            {editingInstance ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      </>
      )}

      {servicesTab === 1 && (
        <Box>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
            Namespaces and services (read-only). Managed under Quality Assurance → Instance Management.
          </Typography>
          {servicesTreeError && <Alert severity="error" sx={{ mb: 2 }}>{servicesTreeError}</Alert>}
          <Box display="flex" gap={1} mb={2}>
            <Button variant="outlined" size="small" onClick={expandAllServices}>Expand all</Button>
            <Button variant="outlined" size="small" onClick={collapseAllServices}>Collapse all</Button>
          </Box>
          {servicesTreeLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress size={40} />
            </Box>
          ) : (
            <Paper variant="outlined" sx={{ overflow: 'visible', pr: 2 }}>
              <List disablePadding>
                {servicesTree.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No namespaces or services yet." secondary="Add them under Quality Assurance → Instance Management." />
                  </ListItem>
                )}
                {servicesTree.map((ns) => {
                  const nsId = String(ns._id);
                  const isExpanded = expandedServicesNamespaces.has(nsId);
                  const services = ns.services || [];
                  const hasServices = services.length > 0;
                  return (
                    <React.Fragment key={nsId}>
                      <ListItem disablePadding sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: isExpanded ? 'action.hover' : undefined }}>
                        <ListItemButton onClick={() => toggleServicesNamespace(nsId)} sx={{ py: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 1 }}>
                            {hasServices ? (isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />) : <Box sx={{ width: 24 }} />}
                            <FolderIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <ListItemText
                              primary={<Typography variant="subtitle1" fontWeight={600}>{ns.name}</Typography>}
                              secondary={ns.description || `${services.length} service(s)`}
                            />
                          </Box>
                        </ListItemButton>
                      </ListItem>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <TableContainer component={Box} sx={{ pl: 4, pr: 3, pb: 1, bgcolor: 'background.default', overflow: 'visible' }}>
                          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, width: '16%' }}>Service Name</TableCell>
                                <TableCell sx={{ fontWeight: 600, width: '36%' }}>Azure Service Name</TableCell>
                                <TableCell sx={{ fontWeight: 600, width: '12%' }}>Release</TableCell>
                                <TableCell sx={{ fontWeight: 600, width: '18%' }}>Branch / Commit</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, width: '18%' }}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {services.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={5} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No services.</TableCell>
                                </TableRow>
                              )}
                              {services.map((svc) => {
                                const cbv = svc.cbvData || {};
                                const serviceName = svc.displayName || cbv.applicationName || svc.baseURL || '—';
                                const commitSha = (cbv.commit && /^[a-fA-F0-9]{7,40}$/.test(String(cbv.commit).trim()))
                                  ? String(cbv.commit).trim()
                                  : ((cbv.branch && /^[a-fA-F0-9]{7,40}$/.test(String(cbv.branch).trim())) ? String(cbv.branch).trim() : null);
                                const repo = svc.repositoryId;
                                const org = 'tr';
                                const repoName = repo?.githubRepoName;
                                const fullRepoName = repoName ? (repoName.includes('/') ? repoName : `${org}/${repoName}`) : null;
                                const commitUrl = commitSha && fullRepoName ? `https://github.com/${fullRepoName}/commit/${commitSha}` : null;
                                const shortHash = commitSha ? (commitSha.length > 7 ? commitSha.slice(0, 7) : commitSha) : null;
                                const branchLabel = cbv.branch && !/^[a-fA-F0-9]{7,40}$/i.test(String(cbv.branch).trim()) ? String(cbv.branch).trim() : null;
                                return (
                                  <TableRow key={svc._id} hover>
                                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 }} title={serviceName}>{serviceName}</TableCell>
                                    <TableCell sx={{ wordBreak: 'break-all', overflowWrap: 'break-word', maxWidth: 0 }} title={svc.baseURL || ''}>
                                      {svc.baseURL ? (
                                        (() => {
                                          const label = getAzureServiceNameFromBaseUrl(svc.baseURL) || svc.baseURL;
                                          const href = getCBVActionUrl(svc.baseURL);
                                          return href ? (
                                            <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
                                              {label}
                                            </Link>
                                          ) : (
                                            <span>{label}</span>
                                          );
                                        })()
                                      ) : (
                                        '—'
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 }}>{cbv.version || '—'}</TableCell>
                                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 0 }}>
                                      {commitUrl ? (
                                        <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                          {branchLabel ? `${branchLabel} (` : ''}
                                          <Link href={commitUrl} target="_blank" rel="noopener noreferrer">{shortHash}</Link>
                                          {branchLabel ? ')' : ''}
                                        </span>
                                      ) : (
                                        (branchLabel || cbv.branch || '—')
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Collapse>
                    </React.Fragment>
                  );
                })}
              </List>
            </Paper>
          )}
        </Box>
      )}

    </Box>
  );
};

export default TestInstancesView;

